import type { LiveEvent } from '../../../types/frontend';
import { humanize } from '../../../shared/labels';

/**
 * Human-facing presentation of raw EventBridge events.
 *
 * The bus delivers a uniform AWS envelope (source / detail-type / account /
 * region / detail) regardless of origin. Operators care about *what happened*
 * and *does it matter*, not the envelope — these helpers translate the raw
 * shape into a friendly type, a severity, and a plain-words summary, with a
 * generic fallback so unknown / non-AWS sources still read sensibly.
 */

/**
 * Reassemble the full AWS EventBridge envelope from a flattened LiveEvent.
 *
 * The bus event is stored flattened (source / detail_type / account / region /
 * payload); the envelope itself is never persisted. This is the single source of
 * truth for that reconstruction — used by both the Raw view and rule-creation's
 * test event, which must agree on the exact shape field-mappings resolve against
 * (`$.detail.X`).
 */
export function toEventEnvelope(event: LiveEvent): Record<string, unknown> {
  return {
    version: '0',
    id: event.id,
    'detail-type': event.detail_type,
    source: event.source,
    account: event.account,
    time: event.timestamp,
    region: event.region,
    resources: event.resources ?? [],
    detail: event.payload,
  };
}

export type CategoryKey =
  | 'security'
  | 'health'
  | 'config'
  | 'compute'
  | 'cost'
  | 'cloud'
  | 'other';

export type SeverityLevel = 'high' | 'medium' | 'low' | 'info';

export interface Severity {
  level: SeverityLevel;
  label: string;
}

const KNOWN_SOURCE_NAMES: Record<string, string> = {
  'cloud2.state-collector': 'Health',
  'cloud2.service-management': 'Service management',
  'aws.guardduty': 'GuardDuty',
  'aws.config': 'Config',
  'aws.health': 'AWS Health',
  'aws.cloudwatch': 'CloudWatch',
  'aws.securityhub': 'Security Hub',
  'aws.inspector2': 'Inspector',
};

export function sourceCategory(source: string): CategoryKey {
  const s = (source || '').toLowerCase();
  if (/(guardduty|securityhub|security|inspector|macie|detective)/.test(s)) return 'security';
  if (/(health|state-collector|heartbeat|monitor)/.test(s)) return 'health';
  if (/(config|compliance)/.test(s)) return 'config';
  if (/(cloudwatch|alarm)/.test(s)) return 'compute';
  if (/(cost|budget|billing)/.test(s)) return 'cost';
  if (/(azure|gcp|webhook)/.test(s)) return 'cloud';
  return 'other';
}

export function friendlySource(source: string): string {
  if (!source || source === 'unknown') return 'Unknown source';
  if (KNOWN_SOURCE_NAMES[source]) return KNOWN_SOURCE_NAMES[source];
  const last = source.split('.').pop() || source;
  return humanize(last);
}

/** The "what happened" label — friendly detail-type, falling back to the source. */
export function friendlyType(event: LiveEvent): string {
  const dt = event.detail_type;
  if (dt && dt !== 'unknown') return humanize(dt);
  return friendlySource(event.source);
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

export function getByPath(obj: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object') return (acc as Record<string, unknown>)[key];
    return undefined;
  }, obj);
}

/** Best-effort severity extracted from the payload. Returns null when unknown. */
export function extractSeverity(event: LiveEvent): Severity | null {
  const p = asRecord(event.payload);

  // GuardDuty: numeric 0–8.9
  if (typeof p.severity === 'number') {
    const n = p.severity;
    if (n >= 7) return { level: 'high', label: 'High' };
    if (n >= 4) return { level: 'medium', label: 'Medium' };
    return { level: 'low', label: 'Low' };
  }

  // AWS Config compliance
  const compliance = getByPath(p, 'newEvaluationResult.complianceType');
  if (typeof compliance === 'string') {
    return compliance === 'NON_COMPLIANT'
      ? { level: 'medium', label: 'Non-compliant' }
      : { level: 'info', label: 'Compliant' };
  }

  // CloudWatch alarm
  if (typeof p.NewStateValue === 'string') {
    return p.NewStateValue === 'ALARM'
      ? { level: 'high', label: 'Alarm' }
      : { level: 'info', label: String(p.NewStateValue) };
  }

  // Generic string severity / priority
  const raw = p.severity ?? p.Severity ?? p.priority ?? p.Priority ?? p.level;
  if (typeof raw === 'string') {
    const v = raw.toLowerCase();
    if (/(critical|fatal|emergency)/.test(v)) return { level: 'high', label: humanize(raw) };
    if (/(high|error|severe)/.test(v)) return { level: 'high', label: humanize(raw) };
    if (/(medium|moderate|warning|warn)/.test(v)) return { level: 'medium', label: humanize(raw) };
    if (/(low|minor)/.test(v)) return { level: 'low', label: humanize(raw) };
    if (/(info|informational|notice)/.test(v)) return { level: 'info', label: humanize(raw) };
    return { level: 'info', label: humanize(raw) };
  }

  return null;
}

const SUMMARY_KEYS = ['title', 'message', 'description', 'summary', 'reason', 'AlarmName', 'eventName'];
const MAX_SUMMARY = 120;

// Envelope / debug fields that carry no human meaning in a summary line.
const NOISE_KEYS = new Set([
  'schema_version', 'schemaversion', 'version', 'timestamp', 'time',
  'xraytraceid', 'traceid', 'tracestate', 'requestid', 'id', 'eventid',
  'correlationid', 'account', 'accountid', 'region', 'source',
]);
const MAX_SCALAR_VALUE = 40;

function clamp(s: string): string {
  const t = s.replace(/\s+/g, ' ').trim();
  return t.length > MAX_SUMMARY ? `${t.slice(0, MAX_SUMMARY - 1)}…` : t;
}

/** Plain-words one-liner. Per-source templates for what we know, generic fallback otherwise. */
export function buildSummary(event: LiveEvent): string {
  const p = asRecord(event.payload);
  const cat = sourceCategory(event.source);

  if (cat === 'security' && (p.title || p.type)) {
    const resource = getByPath(p, 'resource.instanceDetails.instanceId') || getByPath(p, 'resource.resourceType');
    const base = String(p.title || p.type);
    return clamp(resource ? `${base} · ${resource}` : base);
  }

  if (cat === 'config') {
    const rule = p.configRuleName || getByPath(p, 'resourceId') || getByPath(p, 'resourceType');
    const result = getByPath(p, 'newEvaluationResult.complianceType');
    if (rule || result) return clamp([rule, result].filter(Boolean).join(' → '));
  }

  if (cat === 'compute' && p.AlarmName) {
    return clamp(`${p.AlarmName}: ${p.NewStateValue ?? ''}`);
  }

  if (cat === 'health') {
    const who = p.product || p.account || p.resource || '';
    const status = p.status || p.state || '';
    if (who || status) return clamp(`${who} ${status}`.trim());
  }

  for (const k of SUMMARY_KEYS) {
    if (typeof p[k] === 'string' && p[k]) return clamp(p[k] as string);
  }

  const scalars = Object.entries(p)
    .filter(([k, v]) =>
      v != null && typeof v !== 'object' &&
      !NOISE_KEYS.has(k.toLowerCase()) &&
      String(v).length <= MAX_SCALAR_VALUE)
    .slice(0, 3)
    .map(([k, v]) => `${k}: ${v}`);
  if (scalars.length) return clamp(scalars.join(' · '));

  return '—';
}

/** Unique dot-paths to leaf values across the loaded events' payloads — for the column picker autocomplete. */
export function collectPayloadPaths(events: LiveEvent[], maxEvents = 200): string[] {
  const paths = new Set<string>();
  const walk = (obj: unknown, prefix: string, depth: number) => {
    if (depth > 4 || !obj || typeof obj !== 'object' || Array.isArray(obj)) return;
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      const path = prefix ? `${prefix}.${k}` : k;
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        walk(v, path, depth + 1);
      } else {
        paths.add(path);
      }
    }
  };
  for (const e of events.slice(0, maxEvents)) walk(e.payload, '', 0);
  return Array.from(paths).sort();
}

export function formatRelativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 5) return 'now';
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}
